import type {
  PlanCatalogItemContract,
  PlanCatalogLimitContract,
} from '../contracts/plan-catalog.contract';
import { UNLIMITED, type PlanCatalog } from '../models/plan-catalog.model';
import type { PlanSlug, PlanTier } from '../models/subscription-status.enum';

const KNOWN_SLUGS: readonly PlanSlug[] = ['starter', 'pro', 'business'] as const;

export function mapPlansContract(raw: unknown): PlanCatalog[] {
  if (!Array.isArray(raw)) return [];
  const result: PlanCatalog[] = [];
  for (const item of raw) {
    const mapped = mapPlanItem(item);
    if (mapped) result.push(mapped);
  }
  result.sort((a, b) => a.tierOrder - b.tierOrder);
  return result;
}

function mapPlanItem(raw: unknown): PlanCatalog | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Partial<PlanCatalogItemContract>;
  const slug = normalizeSlug(item.slug);
  if (!slug) return null;
  const tier = normalizeTier(item.tierName, slug);

  const tierOrder =
    typeof item.tier === 'number' && Number.isFinite(item.tier)
      ? item.tier
      : tierOrderFromSlug(slug);

  const limits = indexLimits(item.limits);

  return {
    slug,
    tier,
    name: (item.name ?? tier).toString().trim() || tier,
    tierOrder,
    monthlyPriceCents: asNumber(item.monthlyPriceCents, 0),
    annualPriceCents: asNumber(item.annualPriceCents, 0),
    annualDiscountPercent: asNumber(item.annualDiscountPercent, 0),
    limits: {
      maxAdmins: resolveQuota(limits, 'MaxAdmins'),
      maxWorkflows: resolveQuota(limits, 'MaxWorkflows'),
      maxAutomations: resolveQuota(limits, 'MaxAutomations'),
      maxWhatsAppPerMonth: resolveQuota(
        limits,
        'MaxWhatsAppPerMonth',
        'MaxWhatsAppMonthly'
      ),
      maxForms: resolveQuota(limits, 'MaxForms'),
      maxProjects: resolveQuota(limits, 'MaxProjects'),
    },
    features: {
      aiParser: resolveFeature(limits, 'AiParserEnabled'),
    },
    recommended: slug === 'pro',
  };
}

function indexLimits(
  limits: PlanCatalogLimitContract[] | null | undefined
): Map<string, PlanCatalogLimitContract> {
  const map = new Map<string, PlanCatalogLimitContract>();
  if (!Array.isArray(limits)) return map;
  for (const limit of limits) {
    if (!limit || typeof limit !== 'object') continue;
    const key = (limit.resource ?? '').toString().trim().toLowerCase();
    if (!key) continue;
    map.set(key, limit);
  }
  return map;
}

function resolveQuota(
  limits: Map<string, PlanCatalogLimitContract>,
  ...resourceCandidates: string[]
): number {
  for (const candidate of resourceCandidates) {
    const limit = limits.get(candidate.toLowerCase());
    if (!limit) continue;
    if (limit.isUnlimited) return UNLIMITED;
    const max = asNumber(limit.max, 0);
    return max < 0 ? UNLIMITED : max;
  }
  return 0;
}

function resolveFeature(
  limits: Map<string, PlanCatalogLimitContract>,
  resource: string
): boolean {
  const limit = limits.get(resource.toLowerCase());
  if (!limit) return false;
  if (limit.isFeatureFlag) return Boolean(limit.isEnabled);
  return asNumber(limit.max, 0) > 0;
}

function normalizeSlug(value: string | undefined): PlanSlug | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return KNOWN_SLUGS.find((s) => s === normalized) ?? null;
}

function normalizeTier(value: string | undefined, fallbackFromSlug: PlanSlug): PlanTier {
  const v = (value ?? '').trim().toLowerCase();
  if (v === 'starter') return 'Starter';
  if (v === 'pro') return 'Pro';
  if (v === 'business') return 'Business';
  if (fallbackFromSlug === 'starter') return 'Starter';
  if (fallbackFromSlug === 'pro') return 'Pro';
  return 'Business';
}

function tierOrderFromSlug(slug: PlanSlug): number {
  if (slug === 'starter') return 1;
  if (slug === 'pro') return 2;
  return 3;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
